import {
  Building2,
  Fingerprint,
  Globe,
  KeyRound,
  LayoutDashboard,
  List,
  Network,
  ScrollText,
  Server,
  Settings,
  Shield,
  Sparkles,
  ToyBrick,
  Users,
  Wrench,
} from "lucide-react";
import { createElement, type SVGProps } from "react";
import { ORG, PROJECT } from "@/app/capabilities/permissions";
import type { SidebarData } from "./types";

const RotatedNetwork = (props: SVGProps<SVGSVGElement>) =>
  createElement(Network, {
    ...props,
    className: [props.className, "rotate-180"].filter(Boolean).join(" "),
  });

export const data: SidebarData = {
  navOverview: [
    {
      id: "dashboard",
      title: "Dashboard",
      url: "/home",
      icon: LayoutDashboard,
    },
    {
      id: "nav-playground",
      title: "Playground",
      url: "/playground/chat",
      icon: Sparkles,
      activeOnChildRoutes: true,
      activeBasePath: "/playground",
      // Playground (chat + execute) exists only to run tools; gate the nav on
      // tools_execute to match the route guards, so it never shows to a caller
      // who can't run a tool.
      requiredPermission: PROJECT.tools.execute,
    },
  ],
  navAgentDeveloper: [
    {
      id: "nav-organization",
      title: "Organization",
      url: "/organization",
      icon: Globe,
      // The page has independently gated sections for org metadata and org
      // members; show the nav entry when either section is reachable.
      requiredOrgPermission: [ORG.orgMetadata.read, ORG.members.read],
    },
    {
      id: "nav-projects",
      title: "Projects",
      url: "/projects",
      icon: Building2,
      items: [
        {
          id: "nav-members",
          title: "Members",
          url: "/project/members",
          icon: Users,
          // The page shows a members card and an invitations card, each gated on
          // its own read token; surface the nav item when either is readable.
          requiredPermission: [PROJECT.members.read, PROJECT.invitations.read],
        },
      ],
    },
    {
      id: "nav-tools",
      title: "Tool catalog",
      url: "/tools",
      icon: Wrench,
      requiredPermission: PROJECT.tools.read,
    },
    {
      id: "nav-mcp-gateways",
      title: "MCP gateways",
      url: "/mcp-gateways",
      icon: RotatedNetwork,
      requiredPermission: PROJECT.mcpGateways.read,
    },
    {
      id: "nav-user-sources",
      title: "User sources",
      url: "/user-sources",
      icon: Fingerprint,
      // TODO: gate this nav item on the user-sources read permission once it
      // lands in the FGA model. Until then project membership is the only gate.
    },
  ],
  navToolDeveloper: [
    {
      id: "nav-servers",
      title: "Servers",
      url: "/servers",
      icon: Server,
      requiredPermission: PROJECT.servers.read,
    },
    {
      id: "nav-secrets",
      title: "Secrets",
      url: "/auth/secrets",
      icon: KeyRound,
      requiredPermission: PROJECT.secrets.read,
    },
    {
      id: "nav-oauth-playground",
      title: "Connections",
      url: "/auth/oauth",
      icon: ToyBrick,
      // No own gate: the group is shown when any sub-item is visible, so a caller
      // with connected-users or user-verification access still sees the group.
      items: [
        {
          id: "nav-connections",
          title: "Connected apps",
          url: "/auth/oauth",
          icon: List,
          activeOnChildRoutes: true,
          requiredPermission: PROJECT.authProviders.read,
        },
        {
          id: "nav-oauth-users",
          title: "Connected users",
          url: "/auth/connected-users",
          icon: Users,
          requiredPermission: PROJECT.userConnections.read,
        },
        {
          id: "nav-user-verification",
          title: "User verification",
          url: "/settings/user-verification",
          icon: Settings,
          requiredPermission: PROJECT.securitySettings.read,
        },
      ],
    },
  ],
  navSecurity: [
    {
      id: "nav-contextual-access",
      title: "Contextual Access",
      url: "/contextual-access",
      icon: Shield,
      requiredPermission: PROJECT.plugins.read,
    },
    {
      id: "nav-audit-logs",
      title: "Audit Logs",
      url: "/audit-logs",
      icon: ScrollText,
      requiredPermission: PROJECT.auditLogs.read,
    },
  ],
};
